/**
 * Side-effect-free helpers for product price quantity calculations.
 *
 * Kept in its own module so unit tests can exercise the pure logic without
 * importing `use-product-price.ts`, which pulls in the full hook dependency
 * graph (Redux, expo-notifications, RN Platform, etc.) and leaves async work
 * open after teardown.
 */
import { sumAppliedTimesApplied } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { IAppliedRewards } from 'state/loyalty/hooks/types';

interface IComputeAvailableRewardSlotsArgs {
  appliedLoyaltyRewards: IAppliedRewards;
  rewardRedemptionLimit: number;
  cartIdEditing?: string;
  rewardBenefitId?: string;
  /**
   * `enableRewardUnstacking` LD flag value. Named `preventRewardStacking` here to match
   * the existing parameter naming in `computeDiscountPrice`.
   */
  preventRewardStacking: boolean;
  /**
   * Engine reward id of the reward being added. Used to detect the same-reward
   * `limitPerOrder` eviction path in `use-product-cart#processRewardRedemption`. Without
   * it, the helper falls back to the global-cap eviction path only.
   */
  incomingRewardId?: string;
  /** Per-order cap on the incoming reward (Sanity `limitPerOrder`). */
  incomingLimitPerOrder?: number | null;
}

interface IAvailableRewardSlots {
  /** Number of redemptions that should be discounted in the price preview. */
  availableRewardSlots: number;
  /**
   * Points belonging to the reward that the add flow will evict to make room for the new
   * one (`use-product-cart#processRewardRedemption`). Callers should treat the user as
   * having `stagedCartPoints + reservedEvictionPointCost` available for redemption math
   * so the preview matches what will actually happen after the swap.
   *
   * Note: like the unified-menu path (`use-product-total-price.ts`), this is the cost of
   * a single redemption — not `pointCost × timesApplied`. The approximation is consistent
   * across pricing paths and conservative for the common preview use case (one new
   * application).
   */
  reservedEvictionPointCost: number;
}

/**
 * Computes how many reward slots are effectively available to discount the current product.
 *
 * Mirrors the unified-menu logic in `use-product-total-price.ts` so the two pricing paths
 * agree on slot accounting:
 *  - Net cap: `rewardRedemptionLimit − sumAppliedTimesApplied(appliedLoyaltyRewards)`.
 *  - Editing add-back: existing applications on the entry being edited are added back
 *    because they will be replaced by the new selection.
 *  - Eviction reservation: when adding a new reward at full capacity, the legacy add flow
 *    (`processRewardRedemption`) evicts an applied reward. The eviction precedence here
 *    matches the add flow:
 *      1. If the same reward is already applied at its `limitPerOrder`, that specific
 *         entry is the one that will be removed (not necessarily the oldest one).
 *      2. Otherwise the oldest applied reward (`Object.keys(...)[0]`) is evicted.
 *    Whichever entry is targeted, reserve 1 slot and return its point cost so the price
 *    preview reflects the post-swap state.
 */
export const computeAvailableRewardSlots = ({
  appliedLoyaltyRewards,
  rewardRedemptionLimit,
  cartIdEditing,
  rewardBenefitId,
  preventRewardStacking,
  incomingRewardId,
  incomingLimitPerOrder,
}: IComputeAvailableRewardSlotsArgs): IAvailableRewardSlots => {
  let availableRewardSlots = Math.max(
    0,
    rewardRedemptionLimit - sumAppliedTimesApplied(appliedLoyaltyRewards)
  );
  if (cartIdEditing && appliedLoyaltyRewards[cartIdEditing]) {
    availableRewardSlots += appliedLoyaltyRewards[cartIdEditing].timesApplied ?? 0;
  }

  let reservedEvictionPointCost = 0;
  if (preventRewardStacking && rewardBenefitId && !cartIdEditing && availableRewardSlots === 0) {
    const evictionTarget = pickEvictionTarget({
      appliedLoyaltyRewards,
      incomingRewardId,
      incomingLimitPerOrder,
    });
    if (evictionTarget) {
      availableRewardSlots = 1;
      reservedEvictionPointCost = evictionTarget.pointCost;
    }
  }

  return { availableRewardSlots, reservedEvictionPointCost };
};

/**
 * Picks the applied reward entry that the add flow will remove to make room for a new
 * application. Mirrors the precedence in `use-product-cart#processRewardRedemption`:
 *
 * 1. `limitPerOrderMet` on the same reward → that entry is removed (any cart position).
 * 2. Otherwise the oldest entry (`Object.keys(...)[0]`) is evicted.
 *
 * Returns `undefined` when there is nothing applied.
 */
const pickEvictionTarget = ({
  appliedLoyaltyRewards,
  incomingRewardId,
  incomingLimitPerOrder,
}: Pick<
  IComputeAvailableRewardSlotsArgs,
  'appliedLoyaltyRewards' | 'incomingRewardId' | 'incomingLimitPerOrder'
>): { cartId: string; pointCost: number } | undefined => {
  if (incomingRewardId && incomingLimitPerOrder) {
    const matchingEntry = Object.entries(appliedLoyaltyRewards).find(
      ([, applied]) => applied.rewardId === incomingRewardId
    );
    if (matchingEntry && matchingEntry[1].timesApplied >= incomingLimitPerOrder) {
      const [cartId, applied] = matchingEntry;
      return { cartId, pointCost: applied.pointCost };
    }
  }

  const oldestKey = Object.keys(appliedLoyaltyRewards)[0];
  const oldest = oldestKey ? appliedLoyaltyRewards[oldestKey] : undefined;
  if (oldest) {
    return { cartId: oldestKey, pointCost: oldest.pointCost };
  }
  return undefined;
};

/**
 * Returns the number of items the price formula should iterate over for a given product.
 *
 * The discount-side cap (`Math.min(availableRewardSlots, productQuantity)` inside
 * `computeDiscountPrice`) and this base-side cap MUST stay in lock-step: the base
 * must be ≥ the discount or `Math.max(base − discount, 0)` collapses to $0 even when
 * the user is past their reward cap. The "+1" preserves the original
 * "preview the next chargeable item" semantics.
 */
export const getBaseQuantityForPricing = ({
  shouldIncludeQuantity,
  productQuantity,
  isReward,
  enableRewardUnstacking,
  availableRewardSlots,
}: {
  shouldIncludeQuantity: boolean;
  productQuantity: number;
  isReward: boolean;
  enableRewardUnstacking: boolean;
  availableRewardSlots: number;
}): number => {
  if (shouldIncludeQuantity) {
    return productQuantity;
  }

  if (isReward && enableRewardUnstacking) {
    return Math.min(productQuantity, availableRewardSlots + 1);
  }

  return 1;
};
